/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import { forwardRef, memo, useState, useRef, useEffect, useMemo, Fragment } from 'react';
import theme from "../../../theme/settings";
import { randomString } from "../../../utils";
import Typography from "./../../typography";
import { cursorPointer, boxBorder, positionAbsolute, positionRelative, displayNone, displayBlock, cursorNoDrop } from "../../../styles/general";
const {
  spacing,
  colors: {
    fill: {
      hover: clFillHover
    },
    system: {
      active: clSystemActive,
      rest: clSystemRest,
      disabled: systemDisabled
    },
    semantic: {
      danger: clDanger
    }
  }
} = theme;
const Radio = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  id,
  disabled,
  readOnly,
  name,
  defaultChecked,
  checked,
  value,
  label,
  className,
  width,
  onChange,
  inputRef,
  inputProps,
  labelProps,
  required,
  ...props
}, ref) => {
  if (!inputRef) {
    inputRef = useRef(null);
  }
  const [unique] = useState(randomString(10, {
    allowNumber: false,
    allowSymbol: false
  }));
  const [checkedState, setCheckedState] = useState(Boolean(checked !== undefined ? checked : defaultChecked));
  const _formCheckInputCSS = formCheckInputCSS(width);
  const _formCheckLabelCSS = formCheckLabelCSS(width);
  const handleChange = e => {
    if (disabled || readOnly) return;
    if (checked === undefined) setCheckedState(true);
    e = {
      value: value,
      target: {
        ...inputRef.current,
        checked: true,
        value: value
      }
    };
    if (onChange) onChange(e);
  };
  useEffect(() => {
    if (checked !== undefined) setCheckedState(Boolean(checked));
  }, [checked]);
  return useMemo(() => {
    return jsx("div", {
      id: 'DGN-Core-Radio-' + (id || unique),
      css: formCheckCSS,
      className: ['DGN-UI-Radio', className].join(' ').trim().replace(/\s+/g, ' '),
      ref: ref,
      ...props
    }, jsx("input", {
      ...inputProps,
      disabled: disabled,
      ref: inputRef,
      name: name,
      hidden: true,
      checked: checkedState,
      type: "radio",
      css: _formCheckInputCSS,
      id: id || unique,
      onChange: handleChange
    }), jsx("label", {
      css: _formCheckLabelCSS,
      className: `${disabled ? 'disabled' : ''} ${readOnly ? 'read-only' : ''}`,
      htmlFor: id || unique,
      onClick: handleChange
    }, jsx(Typography, {
      color: disabled ? systemDisabled : '',
      type: 'p1',
      ...labelProps
    }, jsx(Fragment, null, label !== null && label !== void 0 ? label : value, required && jsx("span", {
      style: {
        color: disabled ? systemDisabled : clDanger
      }
    }, "*")))));
  }, [disabled, onChange, readOnly, checkedState]);
}));
const formCheckCSS = css`
    ${displayBlock};
    padding: ${spacing(0.5)}px;
`;
const formCheckInputCSS = width => css`
    ${positionAbsolute};
    ${boxBorder};
    ${displayNone};
    &:checked ~ label:after {
        ${boxBorder};
        ${positionAbsolute};
        width: ${width / 2}px;
        height: ${width / 2}px;
        left: ${width / 4}px;
        background: ${clSystemActive};
        border-radius: 50%;
        top: 50%;
        transform: translateY(-50%);
        content: '';
        transition: 0.28s ease;
    }
    &:checked ~ label:before {
        border-color: ${clSystemActive};
    }
`;
const formCheckLabelCSS = width => css`
    ${displayBlock};
    ${positionRelative};
    ${cursorPointer};
    margin-bottom: 0 !important;
    padding-left: ${width + spacing(1.5)}px;
    min-height: ${width}px;
    &:hover:not(.disabled):not(.read-only)::before {
        box-shadow: 0 0 0 2px ${clFillHover};
        background: ${clFillHover};
    }
    &.disabled,
    &.read-only {
        ${cursorNoDrop};
    }
    &.disabled {
        color: ${systemDisabled};
        &:after {
            background: ${systemDisabled} !important;
        }
        &:before {
            border-color: ${systemDisabled} !important;
        }
    }
    &:before {
        ${positionAbsolute};
        ${boxBorder};
        width: ${width}px;
        height: ${width}px;
        border-radius: 50%;
        top: 50%;
        transform: translateY(-50%);
        left: 0;
        content: '';
        border: 2px solid ${clSystemRest};
        transition: 0.28s ease;
    }
`;
Radio.defaultProps = {
  disabled: false,
  readOnly: false,
  defaultChecked: false,
  className: '',
  width: 20,
  labelProps: {}
};
Radio.propTypes = {
  /** If `true`, the component is disabled. */
  disabled: PropTypes.bool,
  /** If `true`, the component is checked. */
  checked: PropTypes.bool,
  /** If `true`, the component is defaultChecked. */
  defaultChecked: PropTypes.bool,
  /** Id is randomized randomly to avoid duplication. */
  id: PropTypes.string,
  /** The id of the `input` element. */
  name: PropTypes.string,
  /** The value of the component. The DOM API casts this to a string. */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** The name of the Radio is displayed on the interface */
  label: PropTypes.string,
  /** The class for Radio component */
  className: PropTypes.string,
  /** Props for input. */
  inputProps: PropTypes.object,
  /** Callback fired when the state is changed.
   *
   * @param {object} event The event source of the callback.
   * You can pull out the new value by accessing `event.target.value` (string).
   * You can pull out the new checked state by accessing `event.target.checked` (boolean).
   *  */
  onChange: PropTypes.func,
  /** Prevent all event if true. */
  readOnly: PropTypes.bool,
  /** Props for Typography of label. */
  labelProps: PropTypes.object,
  /** If `true`, the label will indicate that the Radio is required. */
  required: PropTypes.bool
};
export default Radio;